import type { StrategyListItemV2 } from "../types/strategy";

export type PositionSortField =
  | "symbol"
  | "state"
  | "duration"
  | "fundingFee"
  | "fundingRate"
  | "profit"
  | "startedAt"
  | "filled"
  | "liqPrice"
  | "margin"
  | "traded";

export type SortDirection = "asc" | "desc";

export interface PositionSortConfig {
  field: PositionSortField;
  direction: SortDirection;
}

const STATE_RANK: Record<string, number> = {
  BuyOpen: 1,
  PurchaseCrypto: 2,
  HedgeSetup: 3,
  Transfer: 4,
  NoTransfer: 5,
  Standby: 6,
  EvaluatePnL: 7,
  SellCrypto: 8,
  SellClose: 9,
  HedgeClose: 10,
  Success: 11,
  Failure: 12,
  Abort: 13,
};

export const getStrategySymbol = (strategy: StrategyListItemV2): string => {
  return strategy.symbol || "";
};

export const getStrategyExchangeName = (
  strategy: StrategyListItemV2,
): string => {
  return strategy.spot_exchange_name || "binance";
};

const getNetProfit = (strategy: StrategyListItemV2): number => {
  return strategy.net_profit_usdt ?? 0;
};

const getTradedVolume = (strategy: StrategyListItemV2): number => {
  return strategy.traded_notional_usdt;
};

const getLiquidationMarginPercentage = (
  strategy: StrategyListItemV2,
): number => {
  return strategy.liquidation_margin_percent ?? 0;
};

const getStateRank = (state: string): number => {
  return STATE_RANK[state] ?? 99;
};

export const comparePositionStrategies = (
  a: StrategyListItemV2,
  b: StrategyListItemV2,
  sortConfig: PositionSortConfig,
): number => {
  let comparison = 0;

  switch (sortConfig.field) {
    case "symbol":
      comparison = getStrategySymbol(a).localeCompare(getStrategySymbol(b));
      break;
    case "state": {
      const rankA = getStateRank(a.current_state);
      const rankB = getStateRank(b.current_state);
      comparison =
        rankA !== rankB
          ? rankA - rankB
          : getStrategySymbol(a).localeCompare(getStrategySymbol(b));
      break;
    }
    case "duration": {
      const detectedA = a.detected_at ?? a.started_at ?? 0;
      const detectedB = b.detected_at ?? b.started_at ?? 0;
      comparison = detectedA - detectedB;
      break;
    }
    case "fundingFee":
      comparison = a.total_funding_fees_usdt - b.total_funding_fees_usdt;
      break;
    case "fundingRate": {
      const rateA = a.funding_rate ?? 0;
      const rateB = b.funding_rate ?? 0;
      comparison = rateA - rateB;
      break;
    }
    case "profit":
      comparison = getNetProfit(a) - getNetProfit(b);
      break;
    case "filled":
      comparison = getTradedVolume(a) - getTradedVolume(b);
      break;
    case "traded":
      comparison = getTradedVolume(a) - getTradedVolume(b);
      break;
    case "liqPrice": {
      const liqPriceA = a.liquidation_price_usdt ?? 0;
      const liqPriceB = b.liquidation_price_usdt ?? 0;
      comparison = liqPriceA - liqPriceB;
      break;
    }
    case "margin":
      comparison =
        getLiquidationMarginPercentage(a) -
        getLiquidationMarginPercentage(b);
      break;
    case "startedAt":
      comparison = a.started_at - b.started_at;
      break;
  }

  return sortConfig.direction === "asc" ? comparison : -comparison;
};
